import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { SeoPageService } from './seo-page.service';

const LS_KEY = 'seo.slugs.v1';
const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * In-memory cache of published SEO-page slugs. Consumed by
 * `nonReservedSlugGuard` to block the `/:slug` catch-all from matching
 * non-whitelisted slugs.
 *
 * Runtime lifecycle (browser):
 * 1. APP_INITIALIZER calls `initialize()`. It reads `localStorage[LS_KEY]`
 *    synchronously so the first navigation (often the same one that
 *    triggered bootstrap) has something to check against.
 * 2. A background fetch to `/api/seo-pages/slugs` replaces the in-memory
 *    set once resolved, and writes the refreshed list back to localStorage.
 * 3. A 30-minute interval keeps long-lived SPA sessions current.
 *
 * SSR / prerender behaviour:
 * - `isReady()` returns false and `has()` is permissive (returns true for
 *   any non-reserved slug). Phase 1 Workstream E will add a server-side
 *   provider that reads `prerender-routes.txt` synchronously; until then,
 *   prerender-time guard decisions are delegated to the component's own
 *   404 handling.
 */
@Injectable({ providedIn: 'root' })
export class SeoPageSlugCacheService {
  private readonly slugs = new Set<string>();
  private loaded = false;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly api: SeoPageService,
  ) {}

  public initialize(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      // Server path: no localStorage, no HTTP. Cache stays empty; guard falls
      // back to permissive mode (component handles 404). Replaced by a
      // prerender-routes.txt-backed provider in Workstream E.
      return Promise.resolve();
    }

    this.hydrateFromLocalStorage();
    // Kick the background refresh without awaiting — the initial localStorage
    // read is enough for first-paint routing decisions.
    void this.refresh();
    this.refreshTimer = setInterval(() => { void this.refresh(); }, REFRESH_INTERVAL_MS);
    return Promise.resolve();
  }

  public isReady(): boolean {
    return this.loaded;
  }

  public has(slug: string): boolean {
    return this.slugs.has(this.normalize(slug));
  }

  public dispose(): void {
    if (this.refreshTimer !== null) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private normalize(slug: string): string {
    return (slug || '').trim().toLowerCase();
  }

  private hydrateFromLocalStorage(): void {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      this.setSlugs(parsed);
    } catch {
      // Corrupted entry — clear it so a future successful fetch writes a clean value.
      try { localStorage.removeItem(LS_KEY); } catch {}
    }
  }

  private async refresh(): Promise<void> {
    try {
      const res = await firstValueFrom(this.api.slugs());
      if (res && Array.isArray(res.slugs)) {
        this.setSlugs(res.slugs);
        try { localStorage.setItem(LS_KEY, JSON.stringify(res.slugs)); } catch {}
      }
    } catch {
      // Network/backend failure is non-fatal — keep whatever is in the cache.
    }
  }

  private setSlugs(list: string[]): void {
    this.slugs.clear();
    for (const s of list) {
      const n = this.normalize(s);
      if (n) this.slugs.add(n);
    }
    this.loaded = true;
  }
}
